import * as I from 'immutable';
import {Async, Loaded, Loading} from 'Core/models/Async';

export enum ControlType {
  Boolean,
  Collection,
  Numeric,
}

export type RiderCodeUnit = string | number;
export type RiderCodeBase = {
  code: string;
  displayName: string;
  units: RiderCodeUnit[];
  controlType?: ControlType;
  targetValue?: any;
  hidden?: boolean;
  disabled?: boolean;
};
export type OrdinaryRiderCode = RiderCodeBase;
export type NewYorkRiderCode = RiderCodeBase;
export type GroupRiderCode = {propertyName: string} & RiderCodeBase;
export type RiderCode = OrdinaryRiderCode | GroupRiderCode | NewYorkRiderCode;

export default class RiderInformationBase {
  public readonly key = 'RiderInformationBase';

  getAvailableRiderUnits = (selectedRiderCode: RiderCode) => {
    if (selectedRiderCode.code) {
      const index = this.riderData.findIndex(riderCode => riderCode.code === selectedRiderCode.code);

      return this.riderData[index]?.units;
    }

    return [];
  };

  getAvailableRiderUnitsByCode = (selectedRiderCode: string) => {
    if (selectedRiderCode) {
      const index = this.riderData.findIndex(riderCode => riderCode.code === selectedRiderCode);

      return this.riderData[index]?.units;
    }

    return [];
  };

  getAvailableRiderCodes = (selectedRiderCode: RiderCode) => {
    const updatedList = this.codes;

    return this.riderData.filter(code => !updatedList.includes(code.code) || code.code === selectedRiderCode.code);
  };

  getAvailableRiderCodesByCode = (selectedRiderCode: string, disjointCodeSet: string[]) => {
    let updatedList = this.codes;

    if (disjointCodeSet !== undefined && this.codes.some(code => disjointCodeSet.includes(code))) {
      updatedList = this.codes.merge(disjointCodeSet);
    }

    return this.riderData.filter(code => !updatedList.includes(code.code) || code.code === selectedRiderCode);
  };

  getRiderCodes = () => {
    return this.codes;
  };

  static changeVariableRiderInformation = (
    riderAmount: number,
    riderCode: string,
    index: number,
    riderAmounts: I.List<number>,
    riderCodes: I.List<string>
  ) => {
    let existingRiderAmountList: I.List<number> = riderAmounts;
    let existingRiderCodeList: I.List<string> = riderCodes;
    let newRiderAmountList: I.List<number>;
    let newRiderCodeList: I.List<string>;

    if (riderCode === '' || (riderAmount && isNaN(riderAmount))) {
      const codeIndices = [] as number[];
      const codeValues = [] as string[];
      const amountIndices = [] as number[];
      const amountValues = [] as number[];
      const size = existingRiderCodeList.size;

      for (let i = 0; i < size; i++) {
        if (i !== index) {
          const codeValue = existingRiderCodeList.get(i);
          const amountValue = existingRiderAmountList.get(i);

          if (codeValue) {
            codeValues.push(codeValue);
            codeIndices.push(i);
          }

          if (amountValue) {
            amountIndices.push(i);
            amountValues.push(amountValue);
          }
        }
      }

      newRiderCodeList = existingRiderCodeList.clear();
      newRiderAmountList = existingRiderAmountList.clear();

      for (let j = 0; j < amountValues.length; j++) {
        newRiderAmountList = newRiderAmountList.set(amountIndices[j], amountValues[j]);
      }

      for (let j = 0; j < codeValues.length; j++) {
        newRiderCodeList = newRiderCodeList.set(codeIndices[j], codeValues[j]);
      }
    } else {
      newRiderAmountList = existingRiderAmountList.set(index, riderAmount);
      newRiderCodeList = existingRiderCodeList.set(index, riderCode);
    }

    return {riderAmounts: newRiderAmountList, riderCodes: newRiderCodeList};
  };

  static changeRiderInformation = (
    riderAmount: number,
    riderCode: string,
    index: number,
    riderAmounts: I.List<number>,
    riderCodes: I.List<string>
  ) => {
    let existingRiderAmountList: I.List<number> = riderAmounts;
    let existingRiderCodeList: I.List<string> = riderCodes;
    let newRiderAmountList: I.List<number>;
    let newRiderCodeList: I.List<string>;

    if (isNaN(riderAmount)) {
      const codeIndices = [] as number[];
      const codeValues = [] as string[];
      const amountIndices = [] as number[];
      const amountValues = [] as number[];
      const size = existingRiderCodeList.size;

      for (let i = 0; i < size; i++) {
        if (i !== index) {
          const codeValue = existingRiderCodeList.get(i);
          const amountValue = existingRiderAmountList.get(i);

          if (codeValue && amountValue) {
            codeValues.push(codeValue);
            codeIndices.push(i);
            amountIndices.push(i);
            amountValues.push(amountValue);
          }
        }
      }

      newRiderCodeList = existingRiderCodeList.clear();
      newRiderAmountList = existingRiderAmountList.clear();

      for (let j = 0; j < amountValues.length; j++) {
        newRiderAmountList = newRiderAmountList.set(amountIndices[j], amountValues[j]);
        newRiderCodeList = newRiderCodeList.set(codeIndices[j], codeValues[j]);
      }
    } else {
      newRiderAmountList = existingRiderAmountList.set(index, riderAmount);
      newRiderCodeList = existingRiderCodeList.set(index, riderCode);
    }

    return {riderAmounts: newRiderAmountList, riderCodes: newRiderCodeList};
  };

  constructor(
    public readonly codes: I.List<string>,
    public readonly amounts: I.List<number>,
    public readonly riderData: RiderCode[]
  ) {}
}

export class UnassignedRiderInformation {
  public readonly key = 'UnassignedRiderInformation';
}

export type RiderInformation = Async<RiderInformationBase> | UnassignedRiderInformation;

type MatchRiderInformationOptions<TReturn> = {
  Loading: () => TReturn;
  Loaded: (riderInformation: RiderInformationBase) => TReturn;
  Error: () => TReturn;
  Unassigned: (riderInformation: []) => TReturn;
};

export const isLoading = (riderInformation: RiderInformation): riderInformation is Loading<RiderInformationBase> =>
  riderInformation.key === 'Loading';

export const isLoaded = (riderInformation: RiderInformation): riderInformation is Loaded<RiderInformationBase> =>
  riderInformation.key === 'Loaded';

export const matchRiderInformation = <TReturn>(options: MatchRiderInformationOptions<TReturn>) => (
  riderInformation: RiderInformation
) => {
  switch (riderInformation.key) {
    case 'UnassignedRiderInformation':
      return options.Unassigned([]);
    case 'Loaded':
      return options.Loaded(riderInformation.Data);
    case 'Loading':
      return options.Loading();
    case 'Conflict':
      return options.Error();
    case 'ServerError':
      return options.Error();
    case 'Unauthorized':
      return options.Error();
  }
};
